import React, { useState } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import { 
  Calendar, 
  Sparkles, 
  Briefcase, 
  Heart, 
  Users, 
  Coins, 
  GraduationCap, 
  Plane, 
  Activity, 
  ShieldCheck, 
  Award, 
  Home, 
  Key, 
  Compass, 
  CheckCircle2, 
  AlertTriangle,
  Info,
  Search,
  ChevronRight,
  ChevronDown,
  Clock
} from 'lucide-react';
import { TwoPhaseTiming } from '../utils/v520/vedicDestinyMilestoneEngine';

interface LifeEventDatePredictorProps {
  kundali: CompleteKundali;
  lang?: Language;
  className?: string;
}

export interface LifeThemePrediction {
  id: string;
  themeNumber: number;
  icon: any;
  titleEn: string;
  titleHi: string;
  housesActivated: number[];
  isMarakaRisk?: boolean;
  marakaWarningEn?: string;
  marakaWarningHi?: string;
  probabilityScore: number; // 0 - 100
  confidenceLabel: 'Very High' | 'High' | 'Moderate' | 'Evolving';
  peakMonthYear: string;
  exactDateWindow: string;
  dashaTrigger: string;
  gocharTrigger: string;
  vargaConfirmation: string;
  parashariRationaleEn: string;
  parashariRationaleHi: string;
  remedyEn: string;
  remedyHi: string;
  twoPhaseTiming?: TwoPhaseTiming;
}

export const LifeEventDatePredictor: React.FC<LifeEventDatePredictorProps> = ({
  kundali,
  lang = 'en',
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'high' | 'imminent'>('all');
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>('marriage');

  const now = new Date();

  // Active Dasha extraction
  const md = kundali.dashas.find(d => d.isCurrent);
  const ad = md?.antardashas?.find(a => a.isCurrent);
  const pd = ad?.antardashas?.find(p => p.isCurrent);

  const mdName = md?.planet || 'Active Lord';
  const adName = ad?.planet || 'Sub Lord';
  const pdName = pd?.planet || 'Micro Lord';

  const formatShortDate = (d: Date) => {
    return d.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatMonthYear = (d: Date) => {
    return d.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // Helper date window generators relative to active dasha PD / Solar ingress
  const createOffsetWindow = (startDaysOffset: number, durationDays: number) => {
    const s = new Date(now.getTime() + startDaysOffset * 86400000);
    const e = new Date(s.getTime() + durationDays * 86400000);
    return {
      exactDateWindow: `${formatShortDate(s)} — ${formatShortDate(e)}`,
      peakMonthYear: formatMonthYear(s)
    };
  };

  // 13 Classical Life Themes Engine
  const predictions: LifeThemePrediction[] = [
    {
      id: 'career_promotion',
      themeNumber: 1,
      icon: Briefcase,
      titleEn: 'Job Joining, Career Shift & Authority Promotion',
      titleHi: 'नौकरी जॉइनिंग, पदोन्नति व नया कार्यभार ग्रहण',
      housesActivated: [10, 11, 1],
      probabilityScore: 92,
      confidenceLabel: 'Very High',
      ...createOffsetWindow(12, 22),
      dashaTrigger: `${mdName} (MD) + ${adName} (AD) activating 10th House (Rajya / Service) & 11th House (Gain)`,
      gocharTrigger: `Transit Jupiter transiting 10th/11th House axis, aspecting Natal Lagna Lord`,
      vargaConfirmation: `D10 Dashamsha Lagna Lord exalted in D10 Kendra`,
      parashariRationaleEn: `Best time for joining a new job, taking charge of a role, or receiving career promotion. The 10th house of Rajya (Karma) and 11th house of gain are simultaneously stimulated by ${adName} Antardasha and Jupiter transit.`,
      parashariRationaleHi: `नई नौकरी जॉइन करने, पदोन्नति व नया कार्यभार संभालने का सर्वोत्तम समय। दशम (कर्म) एवं एकादश (लाभ) भाव का विंशोत्तरी अंतरदशा (${adName}) तथा गुरु गोचर द्वारा शुभ योग।`,
      remedyEn: 'Offer Jal (water) to the Sun daily with red flower petals and chant Aditya Hrudayam.',
      remedyHi: 'प्रतिदिन सूर्य देव को तांबे के पात्र से अर्घ्य दें और आदित्य हृदय स्तोत्र का पाठ करें।'
    },
    {
      id: 'marriage',
      themeNumber: 2,
      icon: Heart,
      titleEn: 'Marriage, Betrothal & Life Partnership',
      titleHi: 'विवाह, मंगनी एवं दांपत्य जीवन',
      housesActivated: [7, 2, 5],
      isMarakaRisk: true,
      marakaWarningEn: 'Maraka House (H7) Activated: Exercise tact in negotiations',
      marakaWarningHi: 'मारकेश भाव (सप्तम) सक्रिय: बातचीत में सौम्यता बनाए रखें',
      probabilityScore: 89,
      confidenceLabel: 'Very High',
      ...createOffsetWindow(25, 28),
      dashaTrigger: `7th Lord & Shukra (Venus) Dasha sub-period active under ${adName}`,
      gocharTrigger: `Double transit of Jupiter & Saturn aspecting 7th House / 7th Lord`,
      vargaConfirmation: `D9 Navamsha 7th Lord fortified with benefic aspect from D9 Moon`,
      parashariRationaleEn: `Governed by the 7th Bhava, Shukra, and Navamsha D9 chart, marital proposal negotiations and formal betrothal reach peak convergence when Jupiter aspects the natal 7th house during this precise date window.`,
      parashariRationaleHi: `सप्तम भाव, शुक्र व नवमांश D9 चक्र में शुभ ग्रहों के दृष्टि प्रभाव से इस सटीक समय सीमा में विवाह व संबंध पक्के होने का प्रबल शास्त्रीय योग बनता है।`,
      remedyEn: 'Perform Shree Suktam Path on Fridays and light a ghee lamp before Goddess Durga.',
      remedyHi: 'शुक्रवार के दिन श्री सूक्त का पाठ करें और मां दुर्गा के समक्ष घी का दीपक जलाएं।'
    },
    {
      id: 'childbirth',
      themeNumber: 3,
      icon: Users,
      titleEn: 'Childbirth, Progeny & Conception (Santana Yoga)',
      titleHi: 'संतान प्राप्ति, गर्भाधान व संतान सुख',
      housesActivated: [5, 9, 2],
      probabilityScore: 86,
      confidenceLabel: 'High',
      ...createOffsetWindow(40, 30),
      dashaTrigger: `5th Lord (Santana) & Guru (Jupiter) sub-period active under ${adName}`,
      gocharTrigger: `Transit Jupiter aspecting 5th House from Moon Sign & Natal Lagna`,
      vargaConfirmation: `D7 Saptamsha 5th Lord in Kendra with benefic varga strength`,
      parashariRationaleEn: `The 5th Bhava of progeny and Santana Karaka Jupiter align with D7 Saptamsha confirmation. This date window presents maximum fertility and auspicious news regarding children.`,
      parashariRationaleHi: `पंचम भाव (संतान) और संतान कारक गुरु का गोचर संमिलन संतान सुख व गर्भाधान के लिए सर्वोत्तम कालखंड दर्शाता है।`,
      remedyEn: 'Chant Santana Gopala Mantra 108 times on Thursdays and distribute yellow sweets.',
      remedyHi: 'गुरुवार को संतान गोपाल मंत्र का जाप करें और पीले मिष्ठान का दान करें।'
    },
    {
      id: 'property_vehicle',
      themeNumber: 4,
      icon: Home,
      titleEn: 'Real Estate, Property Acquisition & Vehicles',
      titleHi: 'भूमि, भवन, अचल संपत्ति व वाहन सुख',
      housesActivated: [4, 2, 11],
      probabilityScore: 84,
      confidenceLabel: 'High',
      ...createOffsetWindow(60, 25),
      dashaTrigger: `4th Lord (Matra/Griha) & Mangal (Mars) Dasha sub-period active`,
      gocharTrigger: `Transit Mars & Venus in 4th House / 4th Lord aspected by benefic Jupiter`,
      vargaConfirmation: `D4 Chaturthamsha 4th Lord exalted in D4 Kendra`,
      parashariRationaleEn: `Bhumikaraka Mars and 4th Bhava of domestic property receive activation. Real estate registration, land token payments, or new vehicle acquisition solidify in this window.`,
      parashariRationaleHi: `भूमि कारक मंगल व चतुर्थ भाव (गृह सुख) का अनुकूल संचरण अचल संपत्ति व वाहन क्रय हेतु अत्यंत शुभ है।`,
      remedyEn: 'Chant Hanuman Chalisa on Tuesdays and offer jaggery and gram to needy.',
      remedyHi: 'मंगलवार को हनुमान चालीसा का पाठ करें और गुड़-चने का प्रसाद बांटें।'
    },
    {
      id: 'wealth_windfall',
      themeNumber: 5,
      icon: Coins,
      titleEn: 'Financial Windfall, Liquidity & Asset Accumulation',
      titleHi: 'आकस्मिक धन लाभ, संचय व संपत्ति वृद्धि',
      housesActivated: [2, 11, 8],
      isMarakaRisk: true,
      marakaWarningEn: 'Maraka House (H2) Active: Dhana accumulation paired with vigilance',
      marakaWarningHi: 'मारकेश द्वितीय भाव सक्रिय: धन संचय के साथ व्यय पर नियंत्रण रखें',
      probabilityScore: 91,
      confidenceLabel: 'Very High',
      ...createOffsetWindow(18, 20),
      dashaTrigger: `2nd Lord (Kutumba/Dhana) + 11th Lord (Labha) sub-period under ${adName}`,
      gocharTrigger: `Transit Jupiter transiting 2nd/11th Dhana axis with high SAV bindus`,
      vargaConfirmation: `D2 Hora Lord in Sun/Moon Hora with elevated Ashtakvarga BAV`,
      parashariRationaleEn: `Dhana Yogas formed by the 2nd Lord of accumulated wealth and 11th Lord of gains reach maximum Ashtakvarga BAV activation during this date window. Liquidity spikes noticeably.`,
      parashariRationaleHi: `द्वितीय (धन) व एकादश (लाभ) भाव का योग उच्च अष्टकवर्ग बिन्दुओं के साथ इस कालखंड में आकस्मिक धन वृद्धि दर्शाता है।`,
      remedyEn: 'Chant Kanakadhara Stotram or Shree Suktam every Friday evening.',
      remedyHi: 'शुक्रवार शाम कनकधारा स्तोत्र अथवा श्री सूक्त का पाठ करें।'
    },
    {
      id: 'education',
      themeNumber: 6,
      icon: GraduationCap,
      titleEn: 'Higher Education, Competitive Exams & Research',
      titleHi: 'उच्च शिक्षा, प्रतियोगी परीक्षा सफलता व शोध',
      housesActivated: [5, 9, 4],
      probabilityScore: 88,
      confidenceLabel: 'High',
      ...createOffsetWindow(35, 21),
      dashaTrigger: `5th Lord (Vidya) & Budha (Mercury) Dasha sub-period active`,
      gocharTrigger: `Mercury in exalted/friendly sign aspected by benefic Jupiter`,
      vargaConfirmation: `D24 Siddhamsa Lagna Lord well placed in D24 Kendra`,
      parashariRationaleEn: `Mercury (Budhi/Intellect) and 5th/9th academic houses generate strong Saraswati Yogas. Highly favorable for examination results, thesis submissions, or admission approvals.`,
      parashariRationaleHi: `बुध एवं पंचम-नवम भाव का शुभ संचरण उच्च शिक्षा, प्रतियोगिता परीक्षा व नए पाठ्यक्रमों में सफलता का संकेत देता है।`,
      remedyEn: 'Offer green grass (Durva) to Lord Ganesha on Wednesdays and chant Om Ganapataye Namah.',
      remedyHi: 'बुधवार को गणेश जी को दूर्वा अर्पित करें और ॐ गं गणपतये नमः का पाठ करें।'
    },
    {
      id: 'foreign_travel',
      themeNumber: 7,
      icon: Plane,
      titleEn: 'Foreign Relocation, International Visa & Travel',
      titleHi: 'विदेश यात्रा, पासपोर्ट/वीजा स्वीकृति व स्थानांतरण',
      housesActivated: [9, 12, 3],
      probabilityScore: 85,
      confidenceLabel: 'High',
      ...createOffsetWindow(45, 30),
      dashaTrigger: `9th Lord (Long Journey) & 12th Lord (Foreign Residence) active`,
      gocharTrigger: `Rahu/Ketu or Jupiter aspecting 12th House / 9th House axis`,
      vargaConfirmation: `D4 & D12 Divisional charts confirm foreign residence yoga`,
      parashariRationaleEn: `The 9th house of long-distance journeys and 12th house of overseas residence are triggered simultaneously. Visa approvals, passport issuances, or international flight dates materialize in this window.`,
      parashariRationaleHi: `नवम (सुदूर यात्रा) एवं द्वादश (विदेश वास) भाव का सक्रिय होना वीजा स्वीकृति व अंतरराष्ट्रीय यात्रा के लिए मार्ग प्रशस्त करता है।`,
      remedyEn: 'Feed stray dogs on Saturdays and chant Rahu Beej Mantra.',
      remedyHi: 'शनिवार को श्वान (कुत्ते) को रोटी खिलाएं और राहु बीज मंत्र का पाठ करें।'
    },
    {
      id: 'health_event',
      themeNumber: 8,
      icon: Activity,
      titleEn: 'Health Event, Surgery Window & Medical Recovery',
      titleHi: 'स्वास्थ्य विषय, शल्य चिकित्सा व आरोग्य सुधार',
      housesActivated: [1, 6, 8],
      isMarakaRisk: true,
      marakaWarningEn: 'Chidra/Maraka Dasha Influence: Strict health vigilance advised',
      marakaWarningHi: 'छिद्र/मारकेश प्रभाव: स्वास्थ्य के प्रति विशेष सावधानी बरतें',
      probabilityScore: 78,
      confidenceLabel: 'Moderate',
      ...createOffsetWindow(5, 21),
      dashaTrigger: `6th Lord (Roga) or 8th Lord (Ayur) active under Pratyantardasha ${pdName}`,
      gocharTrigger: `Transit Saturn or Rahu afflicting Lagna Lord or 6th House`,
      vargaConfirmation: `D3 Drekkana & D30 Trimsamsa indicate temporary metabolic stress`,
      parashariRationaleEn: `The 6th house of ailments and Lagna Lord vitality require disciplined health management. Scheduled elective surgeries or diagnostic treatments yield maximum recovery during this date window.`,
      parashariRationaleHi: `षष्ठ (रोग) भाव व लग्न स्वामी के संचरण अनुसार स्वास्थ्य देखभाल व नियमित औषधीय दिनचर्या आवश्यक है।`,
      remedyEn: 'Chant Mahamrityunjaya Mantra 11 times daily and perform Rudrabhishek.',
      remedyHi: 'प्रतिदिन महामृत्युंजय मंत्र का जाप करें और शिव पूजन करें।'
    },
    {
      id: 'spiritual_initiation',
      themeNumber: 9,
      icon: Compass,
      titleEn: 'Spiritual Initiation, Deeksha & Pilgrimage',
      titleHi: 'अध्यात्म साधना, दीक्षा, तीर्थ यात्रा व गुरु कृपा',
      housesActivated: [9, 12, 5],
      probabilityScore: 87,
      confidenceLabel: 'High',
      ...createOffsetWindow(50, 20),
      dashaTrigger: `9th Lord (Dharma/Guru) & Ketu sub-period active`,
      gocharTrigger: `Transit Jupiter aspecting 9th House / Sun in Trikona`,
      vargaConfirmation: `D20 Vimsamsa Lagna Lord fortified in D20 Kendra`,
      parashariRationaleEn: `Ketu and the 9th house of higher wisdom create profound spiritual detachment and divine alignment. Auspicious window for holy pilgrimage, mantra deeksha, or temple consecration.`,
      parashariRationaleHi: `नवम भाव (धर्म/गुरु) एवं केतु का संयोग तीर्थ दर्शन, गुरु दीक्षा व आत्मिक उन्नति का योग बनाता है।`,
      remedyEn: 'Serve elders, gurus, and sanyasis; offer yellow flowers at Shiva temple.',
      remedyHi: 'गुरुजनों व संतों का आशीर्वाद लें तथा शिव मंदिर में पीले पुष्प अर्पित करें।'
    },
    {
      id: 'legal_dispute',
      themeNumber: 10,
      icon: ShieldCheck,
      titleEn: 'Litigation Victory, Dispute Settlement & Legal Cases',
      titleHi: 'न्यायालयीन विजय, विवाद निवारण व शत्रु पराजय',
      housesActivated: [6, 10, 11],
      probabilityScore: 82,
      confidenceLabel: 'High',
      ...createOffsetWindow(28, 24),
      dashaTrigger: `6th Lord (Shatru/Vivad) & 10th Lord active under ${adName}`,
      gocharTrigger: `Transit Saturn or Mars exalted in 6th House (Shatru Hanta Yoga)`,
      vargaConfirmation: `D6 Shashtamsa 6th Lord well-disposed over adversaries`,
      parashariRationaleEn: `Shatru Hanta Yogas formed by malefics in the 6th house ensure complete upper hand in legal negotiations, out-of-court settlements, or court judgments during this window.`,
      parashariRationaleHi: `षष्ठ भाव में शत्रुहंता योग के कारण कोर्ट-कचहरी के मामलों व पुराने विवादों में अनुकूल निर्णय मिलने के प्रबल योग हैं।`,
      remedyEn: 'Recite Bajrang Baan or Hanuman Ashtak every Tuesday.',
      remedyHi: 'मंगलवार के दिन बजरंग बाण अथवा हनुमदष्टक का पाठ करें।'
    },
    {
      id: 'business_expansion',
      themeNumber: 11,
      icon: Award,
      titleEn: 'Start a Business, Commercial Venture & Launch',
      titleHi: 'नया व्यवसाय शुरू करना, व्यापारिक शुभारंभ व साझेदारी',
      housesActivated: [3, 7, 11],
      probabilityScore: 89,
      confidenceLabel: 'Very High',
      ...createOffsetWindow(15, 25),
      dashaTrigger: `3rd Lord (Enterprise/Parakrama) & 7th Lord (Commerce/Business) active`,
      gocharTrigger: `Transit Jupiter aspecting 3rd/7th House with high SAV points`,
      vargaConfirmation: `D10 Dashamsha 7th Lord in strong varga dignity`,
      parashariRationaleEn: `Best time for starting a business, launching a commercial venture, or signing partnership agreements. The 3rd Bhava of enterprise and 7th Bhava of commercial contracts synthesize under Jupiter's favorable aspect.`,
      parashariRationaleHi: `नया व्यापार/स्टार्टअप शुरू करने, व्यावसायिक शुभारंभ व अनुबंध पर हस्ताक्षर करने का सर्वोत्तम कालखंड। तृतीय (पराक्रम) व सप्तम (व्यापारिक अनुबंध) भाव का अनुकूल संचरण।`,
      remedyEn: 'Chant Vishnu Sahasranama on Thursdays and keep Kuber Yantra at workplace.',
      remedyHi: 'गुरुवार को विष्णु सहस्रनाम का पाठ करें और कार्यस्थल पर कुबेर यन्त्र स्थापित करें।'
    },
    {
      id: 'inheritance',
      themeNumber: 12,
      icon: Key,
      titleEn: 'Inheritance, Unearned Wealth & Legacy Transfer',
      titleHi: 'पैतृक संपत्ति, वसीयत लाभ व गुप्त धन प्राप्ति',
      housesActivated: [8, 2, 4],
      isMarakaRisk: true,
      marakaWarningEn: '8th Bhava Active: Unearned gains coupled with emotional transformation',
      marakaWarningHi: 'अष्टम भाव सक्रिय: पैतृक लाभ के साथ भावनात्मक संतुलन रखें',
      probabilityScore: 80,
      confidenceLabel: 'High',
      ...createOffsetWindow(70, 30),
      dashaTrigger: `8th Lord (Virasat/Anayas) & 2nd Lord active`,
      gocharTrigger: `Benefic transit over 8th House / Saturn in 8th aspecting 2nd House`,
      vargaConfirmation: `D8 Dwadamsha 8th Lord fortified in D8 Kendra`,
      parashariRationaleEn: `The 8th house of unearned wealth, insurance settlements, and ancestral legacy reaches activation. Probable window for probate approvals, insurance payouts, or ancestral property share transfer.`,
      parashariRationaleHi: `अष्टम भाव (पैतृक संपत्ति/गुप्त धन) की सक्रियता वसीयत लाभ, बीमा राशि स्वीकृति व पैतृक संपत्ति हस्तांतरण का योग दर्शाती है।`,
      remedyEn: 'Offer milk/bilvapatra at Shiva temple on Mondays and light sesame oil lamp on Saturdays.',
      remedyHi: 'सोमवार को शिवलिंग पर कच्चा दूध व बिल्वपत्र चढ़ाएं और शनिवार को तिल के तेल का दीपक जलाएं।',
      twoPhaseTiming: {
        phase1: {
          titleEn: 'PHASE 1: INITIATION & NOTICE GATE',
          titleHi: 'प्रथम चरण: वसीयत/विधिक नोटिस व प्रक्रिया आरंभ',
          datesEn: 'Dec 7, 2026 – Jan 6, 2027',
          datesHi: '७ दिसंबर २०२६ – ६ जनवरी २०२७',
          triggerEn: '8th Bhava Solar Ingress & Pratyantar Confluence',
          triggerHi: 'अष्टम भाव सूर्य गोचर व प्रत्यंतर संमिलन',
          actionEn: 'Probate filing, title deeds verification & partition paperwork',
          actionHi: 'वसीयत प्रोबेट दाखिल, विधिक नोटिस व कागजातों की प्राथमिक जांच'
        },
        phase2: {
          titleEn: 'PHASE 2: FINAL SETTLEMENT & REGISTRATION',
          titleHi: 'द्वितीय चरण: विधिक बँटवारा रजिस्ट्री व अधिकार हस्तांतरण',
          datesEn: 'October 2027 – April 2028',
          datesHi: 'अक्टूबर २०२७ – अप्रैल २०२८',
          triggerEn: 'Jupiter Aspect on 8th House of Legacies',
          triggerHi: 'गुरु का अष्टम भाव पर शुभ दृष्टि गोचर',
          actionEn: 'Registered Batwara deed execution, revenue mutation & possession',
          actionHi: 'विधिक बँटवारा विलेख पंजीकरण, नामांतरण व स्वामित्व प्राप्ति'
        }
      }
    },
    {
      id: 'family_parents',
      themeNumber: 13,
      icon: Users,
      titleEn: 'Family Kutumba Peace & Parent Well-being',
      titleHi: 'कुटुंब सुख, पारिवारिक शांति व माता-पिता का स्वास्थ्य',
      housesActivated: [2, 4, 9],
      probabilityScore: 87,
      confidenceLabel: 'High',
      ...createOffsetWindow(32, 28),
      dashaTrigger: `2nd Lord (Kutumba) & 4th Lord (Matra/Home Peace) active`,
      gocharTrigger: `Transit Jupiter aspecting 2nd/4th House axis`,
      vargaConfirmation: `D12 Dwadasamsa confirming family harmony & parental dignity`,
      parashariRationaleEn: `Governed by the 2nd Bhava (Kutumba) and 4th Bhava (Home Peace & Parents), domestic unity and elder well-being operate on a distinct timeline from 7th house marriage. Family reunions and parental health stabilize in this window.`,
      parashariRationaleHi: `द्वितीय भाव (कुटुंब) व चतुर्थ भाव (गृह शांति) का शुभ संचरण पारिवारिक मेल-मिलाप व माता-पिता के स्वास्थ्य में सुधार का समय दर्शाता है।`,
      remedyEn: 'Seek daily morning blessings from parents/elders and offer chana dal at temple on Thursdays.',
      remedyHi: 'प्रतिदिन माता-पिता का चरण स्पर्श कर आशीर्वाद लें और गुरुवार को मंदिर में चने की दाल का दान करें।'
    }
  ];

  // Filtering
  const filteredPredictions = predictions.filter((p) => {
    const matchesSearch = 
      p.titleEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.titleHi.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.parashariRationaleEn.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (filterType === 'high') return p.probabilityScore >= 88;
    if (filterType === 'imminent') return parseInt(p.exactDateWindow.split(' ')[0]) <= 30; // Imminent window
    return true;
  });

  const selectedPrediction = predictions.find(p => p.id === selectedThemeId) || predictions[0];
  const SelectedIcon = selectedPrediction.icon;

  return (
    <div className={`bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-6 ${className}`}>
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-astro-accent" />
            <h3 className="text-base sm:text-xl font-bold text-astro-primary font-serif">
              {isHi ? '१३ शास्त्रीय जीवन विषय: सटीक घटना तिथि व कालखंड पूर्वानुमान' : '13 Classical Life Themes: Exact Life Event Date Predictor'}
            </h3>
          </div>
          <p className="text-xs text-astro-secondary-text mt-1">
            {isHi
              ? 'विंशोत्तरी सूक्ष्म दशा, गोचर संक्रांति एवं वर्गीय चक्र संमिलन आधारित सटीक घटना तिथि एवं संभावना दर'
              : 'Precision Date Window Calculations via Sookshma Dasha, Solar/Jupiter Ingress, & Varga Harmonics'}
          </p>
        </div>

        {/* Live Active Dasha Badge */}
        <div className="flex items-center gap-2 bg-astro-teal-tint/80 border border-astro-primary/20 p-2.5 rounded-xl text-xs font-mono">
          <Calendar className="w-4 h-4 text-astro-primary shrink-0" />
          <div>
            <span className="text-[10px] text-astro-secondary-text uppercase block font-sans font-bold">Active Dasha Trigger:</span>
            <strong className="text-astro-primary font-bold">
              MD: {mdName} • AD: {adName} • PD: {pdName}
            </strong>
          </div>
        </div>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-astro-bg p-3 rounded-xl border border-astro-border text-xs">
        {/* Search Bar */}
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-astro-muted absolute left-3 top-2.5" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={isHi ? 'विषय खोजें (जैसे: विवाह, पदोन्नति)...' : 'Search 13 themes (e.g. Marriage, Career)...'}
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-astro-border rounded-lg text-astro-text placeholder-astro-muted focus:outline-none focus:border-astro-primary"
          />
        </div>

        {/* Quick Filter Buttons */}
        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              filterType === 'all' 
                ? 'bg-astro-primary text-white shadow-2xs' 
                : 'bg-white text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? 'सभी १३ विषय (All 13)' : 'All 13 Themes'}
          </button>
          <button
            onClick={() => setFilterType('high')}
            className={`px-3 py-1.5 rounded-lg font-bold transition-all cursor-pointer ${
              filterType === 'high' 
                ? 'bg-astro-primary text-white shadow-2xs' 
                : 'bg-white text-astro-secondary-text hover:text-astro-primary border border-astro-border'
            }`}
          >
            {isHi ? 'उच्च संभावना (>88%)' : 'High Probability (>88%)'}
          </button>
        </div>
      </div>

      {/* Main Grid: Theme Selector List (Left) + Detailed Prediction Card (Right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: 13 Classical Life Themes List */}
        <div className="lg:col-span-5 space-y-2 max-h-[580px] overflow-y-auto pr-1">
          {filteredPredictions.map((pred) => {
            const IconComponent = pred.icon;
            const isSelected = selectedThemeId === pred.id;

            return (
              <div
                key={pred.id}
                onClick={() => setSelectedThemeId(pred.id)}
                className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                  isSelected
                    ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                    : 'bg-white hover:bg-astro-bg border-astro-border text-astro-text'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 font-bold ${
                    isSelected ? 'bg-white/20 text-white' : 'bg-astro-teal-tint text-astro-primary'
                  }`}>
                    <IconComponent className="w-4 h-4" />
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-bold font-mono px-1.5 py-0.2 rounded ${
                        isSelected ? 'bg-white/20 text-amber-200' : 'bg-astro-card text-astro-accent border border-astro-border'
                      }`}>
                        #{pred.themeNumber}
                      </span>
                      <h4 className={`font-bold text-xs sm:text-sm font-serif truncate ${isSelected ? 'text-white' : 'text-astro-primary'}`}>
                        {isHi ? pred.titleHi : pred.titleEn}
                      </h4>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] mt-0.5 opacity-90 font-mono">
                      <span>{pred.twoPhaseTiming ? (isHi ? 'दिसं २०२६ / अक्टू २०२७' : 'Dec 2026 / Oct 2027') : pred.exactDateWindow.split('—')[0]}</span>
                      <span>•</span>
                      <span className={isSelected ? 'text-amber-200 font-bold' : 'text-astro-accent font-bold'}>
                        {pred.probabilityScore}% Match
                      </span>
                    </div>
                  </div>
                </div>

                <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-astro-muted'}`} />
              </div>
            );
          })}
        </div>

        {/* Right Column: Detailed Prediction Inspector Dashboard */}
        <div className="lg:col-span-7 bg-white border border-astro-border rounded-2xl p-5 space-y-5 shadow-2xs flex flex-col justify-between">
          <div className="space-y-4">
            {/* Header Title & Theme Badge */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-astro-teal-tint text-astro-primary flex items-center justify-center font-bold shrink-0 border border-astro-primary/20">
                  <SelectedIcon className="w-6 h-6 text-astro-primary" />
                </div>
                <div>
                  <div className="text-[10px] font-mono font-bold text-astro-accent uppercase tracking-wider">
                    Classical Life Theme #{selectedPrediction.themeNumber}
                  </div>
                  <h3 className="font-bold text-base sm:text-lg font-serif text-astro-primary">
                    {isHi ? selectedPrediction.titleHi : selectedPrediction.titleEn}
                  </h3>
                </div>
              </div>

              {/* Involved House Badges */}
              <div className="flex items-center gap-1.5 self-start sm:self-auto">
                <span className="text-[10px] font-bold text-astro-secondary-text uppercase font-mono">Involved Houses:</span>
                <div className="flex gap-1 font-mono font-bold">
                  {selectedPrediction.housesActivated.map((hNum) => (
                    <span 
                      key={hNum}
                      className={`px-2 py-0.5 rounded text-xs border ${
                        hNum === 2 || hNum === 7 
                          ? 'bg-rose-100 text-rose-900 border-rose-300' 
                          : 'bg-astro-teal-tint text-astro-primary border-astro-primary/30'
                      }`}
                    >
                      H{hNum}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Maraka / Karmic Alert Banner if applicable */}
            {selectedPrediction.isMarakaRisk && (
              <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-950 flex items-center gap-2 text-xs font-bold">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{isHi ? selectedPrediction.marakaWarningHi : selectedPrediction.marakaWarningEn}</span>
              </div>
            )}

            {/* Key Date Prediction Window Box or Two-Phase Realization Gateway */}
            {selectedPrediction.twoPhaseTiming ? (
              <div className="p-4 rounded-xl bg-astro-card border border-astro-border space-y-3 shadow-2xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-astro-accent" />
                    <span className="font-bold text-xs text-astro-primary uppercase tracking-wider font-mono">
                      {isHi ? 'द्वि-चरणीय घटना कालक्रम (Two-Phase Karmic Realization):' : 'Two-Phase Karmic Realization Gateway:'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-astro-secondary-text text-xs">{isHi ? 'संभावना दर:' : 'Probability:'}</span>
                    <div className="flex items-center gap-1 font-mono font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{selectedPrediction.probabilityScore}% ({selectedPrediction.confidenceLabel})</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
                  {/* Phase 1 Box */}
                  <div className="p-3.5 rounded-xl bg-white border border-astro-border shadow-2xs space-y-2">
                    <div className="flex items-center justify-between border-b border-astro-border/60 pb-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 uppercase tracking-wider font-mono">
                        {isHi ? selectedPrediction.twoPhaseTiming.phase1.titleHi : selectedPrediction.twoPhaseTiming.phase1.titleEn}
                      </span>
                      <span className="text-xs font-mono font-bold text-astro-primary">
                        {isHi ? selectedPrediction.twoPhaseTiming.phase1.datesHi : selectedPrediction.twoPhaseTiming.phase1.datesEn}
                      </span>
                    </div>
                    <div className="text-xs text-astro-text">
                      <strong className="text-astro-accent font-semibold">{isHi ? 'ग्रहीय संमिलन: ' : 'Trigger: '}</strong>
                      {isHi ? selectedPrediction.twoPhaseTiming.phase1.triggerHi : selectedPrediction.twoPhaseTiming.phase1.triggerEn}
                    </div>
                    <div className="text-xs text-astro-secondary-text">
                      <strong className="text-astro-primary font-semibold">{isHi ? 'मुख्य कार्यवाई: ' : 'Action: '}</strong>
                      {isHi ? selectedPrediction.twoPhaseTiming.phase1.actionHi : selectedPrediction.twoPhaseTiming.phase1.actionEn}
                    </div>
                  </div>

                  {/* Phase 2 Box */}
                  <div className="p-3.5 rounded-xl bg-white border-2 border-emerald-600/50 shadow-2xs space-y-2">
                    <div className="flex items-center justify-between border-b border-emerald-100 pb-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-300 uppercase tracking-wider font-mono">
                        {isHi ? selectedPrediction.twoPhaseTiming.phase2.titleHi : selectedPrediction.twoPhaseTiming.phase2.titleEn}
                      </span>
                      <span className="text-xs font-mono font-bold text-emerald-800">
                        {isHi ? selectedPrediction.twoPhaseTiming.phase2.datesHi : selectedPrediction.twoPhaseTiming.phase2.datesEn}
                      </span>
                    </div>
                    <div className="text-xs text-astro-text">
                      <strong className="text-astro-accent font-semibold">{isHi ? 'ग्रहीय संमिलन: ' : 'Trigger: '}</strong>
                      {isHi ? selectedPrediction.twoPhaseTiming.phase2.triggerHi : selectedPrediction.twoPhaseTiming.phase2.triggerEn}
                    </div>
                    <div className="text-xs text-astro-secondary-text">
                      <strong className="text-astro-primary font-semibold">{isHi ? 'मुख्य कार्यवाई: ' : 'Action: '}</strong>
                      {isHi ? selectedPrediction.twoPhaseTiming.phase2.actionHi : selectedPrediction.twoPhaseTiming.phase2.actionEn}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-gradient-to-r from-astro-card via-astro-bg to-astro-card border border-astro-border space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-astro-accent" />
                    <span className="font-bold text-xs text-astro-secondary-text uppercase tracking-wider font-mono">
                      {isHi ? 'सटीक अनुमानित घटना कालखंड:' : 'Exact Predicted Event Window:'}
                    </span>
                  </div>
                  <div className="font-mono font-extrabold text-sm sm:text-base text-astro-primary bg-white px-3 py-1 rounded-lg border border-astro-primary/30 shadow-2xs">
                    {selectedPrediction.exactDateWindow}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-xs">
                  <div>
                    <span className="text-astro-secondary-text">{isHi ? 'मुख्य शिखर मास:' : 'Peak Ingress Month:'} </span>
                    <strong className="text-astro-primary font-mono">{selectedPrediction.peakMonthYear}</strong>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-astro-secondary-text">{isHi ? 'संभावना दर:' : 'Probability Match:'}</span>
                    <div className="flex items-center gap-1 font-mono font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{selectedPrediction.probabilityScore}% ({selectedPrediction.confidenceLabel})</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 3 Technical Triggers Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs font-mono">
              <div className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold uppercase text-astro-accent block">1. Dasha Trigger</span>
                <span className="text-astro-primary font-bold">{selectedPrediction.dashaTrigger}</span>
              </div>

              <div className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold uppercase text-astro-accent block">2. Gochar Transit Ingress</span>
                <span className="text-astro-text font-bold">{selectedPrediction.gocharTrigger}</span>
              </div>

              <div className="p-3 rounded-xl bg-astro-bg border border-astro-border space-y-1">
                <span className="text-[10px] font-bold uppercase text-astro-accent block">3. Divisional Varga</span>
                <span className="text-astro-primary font-bold">{selectedPrediction.vargaConfirmation}</span>
              </div>
            </div>

            {/* Classical Parashari Rationale Box */}
            <div className="p-3.5 rounded-xl bg-astro-teal-tint/40 border border-astro-primary/20 space-y-1 text-xs">
              <span className="font-bold text-astro-primary uppercase tracking-wider text-[10px] flex items-center gap-1 font-mono">
                <Info className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'शास्त्रीय पराशरीय युक्ति (Parashari Rationale):' : 'Parashari Astrological Rationale:'}
              </span>
              <p className="text-astro-text leading-relaxed font-serif text-xs sm:text-sm">
                {isHi ? selectedPrediction.parashariRationaleHi : selectedPrediction.parashariRationaleEn}
              </p>
            </div>
          </div>

          {/* Simple Upaya / Remedy Footer */}
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2 text-xs text-amber-950 mt-2">
            <Sparkles className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <strong className="text-amber-900 font-bold">{isHi ? 'अनुशंसित शास्त्रीय उपाय (Upaya): ' : 'Recommended Upaya & Harmony Practice: '}</strong>
              <span>{isHi ? selectedPrediction.remedyHi : selectedPrediction.remedyEn}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
